import { getAccess } from '../../services/UserService';
import { getPublishedStringOverrides } from './customizationApi';

/**
 * Applies a resolved override map ({ <dotted strings-file path>: <replacement
 * text> }) to window.myinsyteresources in memory, replacing the shipped text for
 * every language. The server produces the map from its StringOverrideDescriptors
 * catalog (the single source of truth), so only sanctioned strings ever arrive
 * here; paths that don't resolve to an existing string are ignored. Returns the
 * number of resource strings replaced.
 */
export const applyStringOverrides = (overrides) => {
  if (!overrides || typeof overrides !== 'object') return 0;

  const resources = window.myinsyteresources;
  if (!resources) return 0;

  let applied = 0;

  for (const [path, text] of Object.entries(overrides)) {
    if (typeof text !== 'string' || text.trim() === '') continue;

    const segments = path.split('.');
    for (const lang of Object.keys(resources)) {
      let node = resources[lang]?.translation;
      for (let i = 0; node && i < segments.length - 1; i++) {
        node = node[segments[i]];
      }
      const leaf = segments[segments.length - 1];
      if (node && typeof node[leaf] === 'string') {
        node[leaf] = text;
        applied++;
      }
    }
  }

  return applied;
};

/**
 * Bootstrap hook: fetches the tenant's published string overrides and applies them
 * to window.myinsyteresources. Must run before i18n.init (InitializeHelpers). Both
 * this check and the server endpoint gate on the RTL_Customization module, so
 * overrides never apply on unlicensed tenants. Failures are swallowed — the app
 * boots with shipped strings.
 */
export const loadAndApplyStringOverrides = async () => {
  try {
    const access = getAccess();
    if (!access || !access.modules?.().RTL_Customization?.()) return;

    const overrides = await getPublishedStringOverrides();
    if (overrides) {
      applyStringOverrides(overrides);
    }
  } catch {
    // Never block app startup on string overrides.
  }
};
